import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  onStartTour: () => void;
  isAdmin?: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
  button: {
    background: 'none', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%',
    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0, transition: 'all 0.2s',
  },
  dropdown: {
    position: 'absolute', top: '100%', right: 0, marginTop: 8,
    background: 'white', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    border: '1px solid #e2e8f0', minWidth: 220, zIndex: 1200, overflow: 'hidden',
    animation: 'helpMenuFade 0.15s ease-out',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
    cursor: 'pointer', fontSize: 14, color: '#333', background: 'white',
    border: 'none', width: '100%', textAlign: 'left' as const,
    transition: 'background 0.15s',
  },
  iconWrapper: {
    width: 32, height: 32, borderRadius: 6, display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  itemLabel: { fontWeight: 500 },
  itemDesc: { fontSize: 11, color: '#888', marginTop: 1 },
  divider: { height: 1, background: '#e2e8f0' },
};

export default function HelpMenu({ onStartTour, isAdmin }: Props) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <div style={styles.wrapper} ref={dropdownRef}>
      <style>{`
        @keyframes helpMenuFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .help-menu-btn:hover { background: rgba(255,255,255,0.15) !important; border-color: rgba(255,255,255,0.5) !important; }
        .help-menu-item:hover { background: #f7fafc !important; }
      `}</style>
      <button
        className="help-menu-btn"
        style={styles.button}
        onClick={() => setOpen(!open)}
        title="Help"
        aria-label="Help menu"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {open && (
        <div style={styles.dropdown}>
          <button
            className="help-menu-item"
            style={styles.dropdownItem}
            onClick={() => { setOpen(false); navigate('/help'); }}
          >
            <div style={{ ...styles.iconWrapper, background: '#ebf8ff' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div>
              <div style={styles.itemLabel}>Documentation</div>
              <div style={styles.itemDesc}>Learn how to use the app</div>
            </div>
          </button>
          {isAdmin && (
            <>
              <div style={styles.divider} />
              <button
                className="help-menu-item"
                style={styles.dropdownItem}
                onClick={() => { setOpen(false); navigate('/help/accounting'); }}
              >
                <div style={{ ...styles.iconWrapper, background: '#faf5ff' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#805ad5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </div>
                <div>
                  <div style={styles.itemLabel}>Accounting Documentation</div>
                  <div style={styles.itemDesc}>Admin guide for the Accounting tab</div>
                </div>
              </button>
            </>
          )}
          <div style={styles.divider} />
          <button
            className="help-menu-item"
            style={styles.dropdownItem}
            onClick={() => { setOpen(false); onStartTour(); }}
          >
            <div style={{ ...styles.iconWrapper, background: '#f0fff4' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polygon points="10 8 16 12 10 16 10 8" />
              </svg>
            </div>
            <div>
              <div style={styles.itemLabel}>Tour</div>
              <div style={styles.itemDesc}>Take a guided walkthrough</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
